import React from 'react';
import { DialogProps } from '@radix-ui/react-dialog';
import { useMutation } from '@apollo/client';

import {
  Modal,
  ModalDescription,
  ModalContent,
  ModalTitle,
  ModalFooter
} from 'epoch-ui/components/Modal/modal';
import { Form } from '@epoch-ui/components/Form';
import { QuestionnaireFields } from '@epoch-components/QuestionnaireFields';
import {
  useQuestionResponseForm,
  QuestionnaireFormValues
} from '@epoch-components/QuestionnaireFields/schema';
import { Button } from '@epoch-ui/components/Button';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { useConfirmUnsavedChanges } from 'epoch-components/UnsavedChangesConfirmationModal';
import {
  UpdateQuestionnaireResponsesMutationVariables,
  UpdateQuestionnaireResponsesMutationResponse,
  UPDATE_QUESTIONNAIRE_RESPONSES_MUTATION
} from 'graphql/mutations/rsvp_control/update-guest-survey-response';

import { useGuestSideSheetContext } from '../guestSideSheetContext';

export interface EditQuestionnaireModalProps extends DialogProps {
  onSubmit?: () => void;
  disabled?: boolean;
}

export const EditQuestionnaireModal: React.FC<EditQuestionnaireModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  disabled
}): JSX.Element => {
  const formId = React.useId();
  const {
    guestData: {
      event: { id: eventId, preEventQuestions: questions },
      guest: { gid, firstName, surveyResponses: responses = [] }
    }
  } = useGuestSideSheetContext();
  const { showToast } = useToaster();
  const { confirmWhen } = useConfirmUnsavedChanges();

  const fieldName = 'questionnaireResponses';
  const { form, parseResponses } = useQuestionResponseForm({
    fieldName,
    questions,
    responses
  });

  const resetFormAndClose = (): void => {
    onOpenChange?.(false);
    form.reset();
  };

  const { isDirty } = form.formState;
  const handleOnOpenChange = (isOpen: boolean): void => {
    if (!isOpen) confirmWhen(isDirty, { onConfirm: resetFormAndClose });
  };

  const [updateQuestionnaireResponses, { loading }] = useMutation<
    UpdateQuestionnaireResponsesMutationResponse,
    UpdateQuestionnaireResponsesMutationVariables
  >(UPDATE_QUESTIONNAIRE_RESPONSES_MUTATION, {
    onCompleted: (): void => {
      onOpenChange?.(false);
      onSubmit?.();
    },
    onError: (): void => {
      showToast('Could not update questionnaire', { variant: ToasterVariant.ERROR });
    }
  });

  const submitResponses = (formValues: {
    questionnaireResponses: QuestionnaireFormValues;
  }): void => {
    const updatedResponses = parseResponses(formValues);
    const input = { gid, eventId, responses: updatedResponses };
    updateQuestionnaireResponses({ variables: { input } });
  };

  return (
    <Modal open={open} onOpenChange={handleOnOpenChange}>
      <ModalContent className="tw-min-w-[675px] tw-gap-5 tw-rounded-[16px]">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <ModalTitle className="tw-mb-0 tw-text-2xl tw-font-medium">Edit Questionnaire</ModalTitle>
          <ModalDescription className="tw-mb-0">{`You're editing ${firstName}'s questionnaire response`}</ModalDescription>
        </div>
        <Form {...form}>
          <form
            id={formId}
            onSubmit={form.handleSubmit(submitResponses)}
            className="tw-flex tw-flex-col tw-gap-6"
          >
            <QuestionnaireFields
              questions={questions}
              fieldNamePrefix={fieldName}
              disabled={disabled}
            />
          </form>
        </Form>
        <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-end">
          <Button
            size="sm"
            className="tw-rounded-[8px]"
            variant="primary"
            loading={loading}
            disabled={disabled}
            type="submit"
            form={formId}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
